import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ShadCNDatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  fromYear?: number;
  toYear?: number;
  placeholder?: string;
  disabled?: boolean;
  disableFutureDates?: boolean;
  disablePastDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
  customDisabledDates?: (date: Date) => boolean;
}

const ShadCNDatePicker: React.FC<ShadCNDatePickerProps> = ({
  value,
  onChange,
  fromYear = 1950,
  toYear = new Date().getFullYear() + 50,
  placeholder = "Pick a date",
  disabled = false,
  disableFutureDates = false,
  disablePastDates = false,
  minDate,
  maxDate,
  customDisabledDates,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getDisabledDates = (date: Date): boolean => {
    // Custom disabled dates function takes priority
    if (customDisabledDates) {
      return customDisabledDates(date);
    }

    // Check min date
    if (minDate && date < minDate) {
      return true;
    }

    // Check max date
    if (maxDate && date > maxDate) {
      return true;
    }

    // Check disable future dates
    if (disableFutureDates && date > new Date()) {
      return true;
    }

    // Check disable past dates
    if (disablePastDates && date < new Date()) {
      return true;
    }

    // Default: disable dates before 1900
    return date < new Date("1900-01-01");
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "flex w-full rounded-md border border-input bg-background py-6 text-base font-medium placeholder:text-muted-foreground ring-offset-background file:border-0 file:bg-transparent  file:font-medium hover:bg-transparent",
            isOpen && "ring-2 ring-ring ring-offset-2",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          {value ? format(value, "PPP") : <span className="text-muted-foreground">{placeholder}</span>}
          <CalendarIcon className={cn("ml-auto h-5 w-5 opacity-50 hover:opacity-100", disabled && "cursor-not-allowed", isOpen && "opacity-100")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={value}
          onSelect={(selectedDate) => {
            onChange(selectedDate);
            setIsOpen(false);
          }}
          startMonth={new Date(fromYear, 0)}
          endMonth={new Date(toYear, 11)}
          defaultMonth={value || new Date()}
          className="rounded-md border shadow-xs"
          disabled={getDisabledDates}
        />
      </PopoverContent>
    </Popover>
  );
};

export default ShadCNDatePicker;
